// Database.jsx

import React, { useEffect, useState, useCallback } from 'react'
import { useApi } from '../../../../api/api'
import { useSelector } from 'react-redux'

import datasourceDefs from '../../../../api/datasourceConfig'
import {
    ScoopTextField,
    ScoopButton,
    ScoopDialog,
    ScoopText,
    ScoopLinearProgress,
    ScoopSelector,
    ScoopRadioButton,
    ScoopTab,
    ScoopTabPanel,
    ScoopTable,
    useToast,
} from '../../../../components/ui'
import { EVENTS, trackEvent } from '../../../../events'
import { useModal, MODALS } from '../../../modals'
import { driverClassMapping, driverPortMapping } from '../../../../constants/databaseDrivers'
import styles from './Database.module.scss'

const bot = datasourceDefs.find((b) => b.name === 'Database')

function getKeyByValue(mapping, value) {
    return Object.keys(mapping).find((key) => mapping[key] === value)
}

function generateShort_UUID() {
    return 'xxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

export const Database = ({
    open,
    onClose,
    stepBack,
    type,
    initialDatasetDetails,
    basicConfiguration,
}) => {
    const toast = useToast()
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const { postData } = useApi(bot.API_URL)
    const { postData: postDataMobileAPI } = useApi()
    const integrationRequestModal = useModal(MODALS.COMMON.REQUEST_INTEGRATION)
    const [connectBy, setConnectBy] = useState('Host')
    const [url, setUrl] = useState(null)
    const [serverHost, setServerHost] = useState('')
    const [port, setPort] = useState('3306')
    const [database, setDatabase] = useState(null)
    const [driver, setDriver] = useState({
        friendlyName: 'MariaDB', // Default to MySQL
        className: driverClassMapping['MariaDB'], // Default class name
    })
    const [dbUser, setDbUser] = useState('admin')
    const [dbPassword, setDBPassword] = useState('')
    const [sqlQuery, setSqlQuery] = useState('SELECT * from ...')

    const [isUrlValid, setIsUrlValid] = useState(true)
    const [isReportTransactional, setIsReportTransactional] = useState(false)
    const [allowMutipleLoads, setAllowMutipleLoads] = useState(false)
    const [reportName, setReportName] = useState('')
    const [reportDescription, setReportDescription] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [nextStep, setNextStep] = useState(false)
    const [tabManager, setTabManager] = useState('0')
    const [queryVerified, setQueryVerified] = useState(false) // Set to true if the query is verified and user should be allowed to create bot

    const [columnDefs, setColumnDefs] = useState([])
    const [rowData, setRowData] = useState([])

    // set if user is editing vs creating new
    const [isEditMode, setIsEditMode] = useState(false)
    const [inboxName, setInboxName] = useState(null) //used only if inboxName is passed in as part of update call
    const [inboxID, setInboxID] = useState(null) //used only if inboxID is passed in as part of update call

    useEffect(() => {
        if (initialDatasetDetails) {
            setIsEditMode(true)
            const JDBCProps = JSON.parse(initialDatasetDetails.reportLink)
            setServerHost(JDBCProps.server)
            setPort(JDBCProps.port)
            setDatabase(JDBCProps.database)
            setDriver({
                friendlyName: getKeyByValue(driverClassMapping, JDBCProps.driverClass),
                className: JDBCProps.driverClass,
            })
            setUrl(JDBCProps.url)
            setDBPassword(JDBCProps.password)
            setDbUser(JDBCProps.username)
            setSqlQuery(JDBCProps.query)
            setReportDescription(initialDatasetDetails.description)
            setReportName(initialDatasetDetails.label)
            setInboxName(initialDatasetDetails.inboxName)
            setIsReportTransactional(initialDatasetDetails.isTransactional || false)
            setAllowMutipleLoads(initialDatasetDetails.enableMultipleSameDayLoads || false)
            setInboxID(initialDatasetDetails.inboxID)
        }
    }, [initialDatasetDetails])

    const handleDriverChange = (event) => {
        const selectedFriendlyName = event.target.value
        const selectedClassName = driverClassMapping[selectedFriendlyName]
        setDriver({
            friendlyName: selectedFriendlyName,
            className: selectedClassName,
        })
        setPort(driverPortMapping[selectedFriendlyName])
    }

    const handleUrlChange = (e) => {
        const newUrl = e.target.value
        setUrl(newUrl)

        // Validate URL
        setIsUrlValid(bot.urlValidation.test(newUrl))
    }

    const testJDBConnection = useCallback(async () => {
        if (!userID) return
        const action = {
            action: 'testJDBC',
            driverClass: driver.className,
            server: serverHost,
            url: url,
            port: port,
            database: database,
            driver: driver.friendlyName,
            username: dbUser,
            password: dbPassword,
        }
        const response = await postDataMobileAPI(action)
        const { result } = response
        if (result) return result
    }, [postDataMobileAPI, userID])

    const handleTestJDBC = () => {
        setIsLoading(true)
        testJDBConnection().then((result) => {
            if (result == 'Success') {
                toast.success('Connected to your DB successfully!')
            } else {
                toast.error(
                    'DB Connection failed. Please ensure both that your DB settings are correct; and, that your DB is accessible to Scoop.'
                )
            }
        })
        setIsLoading(false)
    }

    const testJDBCQuery = useCallback(async () => {
        if (!userID) return
        const action = {
            action: 'testJDBCQuery',
            driverClass: driver.className,
            server: serverHost,
            url: url,
            username: dbUser,
            password: dbPassword,
            database: database,
            driver: driver.friendlyName,
            port: port,
            query: sqlQuery,
        }
        const result = await postDataMobileAPI(action)

        // Return an object that indicates whether the operation was successful and includes the message
        if (result?.columns && result?.rows) {
            return { success: true, response: result }
        } else {
            return { success: false, response: result.error }
        }
        return { success: false, message: 'An unknown error occurred' }
    }, [postDataMobileAPI, userID, driver.className, serverHost, dbUser, dbPassword])

    const handleJDBCQuery = () => {
        setIsLoading(true)
        testJDBCQuery()
            .then(({ success, response }) => {
                if (success) {
                    setQueryVerified(true)
                    // Transform columns for ScoopTable
                    const transformedColumnDefs = response.columns.map((col) => ({
                        field: col.columnName,
                        header: col.columnName,
                    }))

                    // Directly use rows as rowData
                    const transformedRowData = response.rows.map((row) => {
                        return response.columns.reduce((acc, col, index) => {
                            acc[col.columnName] = row[index]
                            return acc
                        }, {})
                    })

                    setColumnDefs(transformedColumnDefs)
                    setRowData(transformedRowData)

                    toast.success('Your query ran successfully, see preview of your data!')
                } else {
                    toast.error(
                        `Oops, unable to run your SQL query (${response}). Please check its syntax, edit, and try again.`
                    )
                }
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    // this is custom specific to the JDBC bot!
    const runBot = async () => {
        const action = {
            action: 'runJDBCBot',
            inboxID: inboxID,
            userID: userID,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        const result = await postData(action)
        return result
    }

    const handleRunBot = () => {
        runBot().then((results) => {
            if (results?.status === 'success') toast.success('App Connector ran successfully!')
            else if (results?.status === 'error')
                toast.info(
                    results?.message ??
                        'The process is taking some time, your new data will become available soon.'
                )
            setNextStep(false)
            onClose()
        })
    }

    function createInboxName(userID, reportName) {
        // Combine userID and label with an underscore
        let combinedName = `${userID}_${reportName.substring(0, 30)}_${generateShort_UUID()}`

        // Replace characters not in the allowed list with an underscore
        // Allowed characters: alphanumeric, plus (+), minus (-), equals (=), dot (.), comma (,), underscore (_), colon (:), at (@)
        let scrubbedName = combinedName.replace(/[^a-zA-Z0-9\+\-\=\.\,\_\:\@]/g, '_')

        return scrubbedName
    }

    const createBot = async () => {
        const JDBCObject = {
            driverClass: driver.className,
            server: serverHost,
            url: url,
            port: port,
            username: dbUser,
            password: dbPassword,
            database: database,
            query: sqlQuery,
        }
        const JDBC = JDBCObject
        const action = {
            action: 'createRobot',
            inboxName: createInboxName(dbUser, reportName),
            label: reportName,
            description: reportDescription,
            keepOnlyCurrent: basicConfiguration.isMostRecent,
            incremental: basicConfiguration.isIncremental,
            enableMultipleSameDayLoads: basicConfiguration.isMultipleLoads,
            workspaceID: workspaceID,
            reportURL: JDBC,
            isTransactional: type === 'Transactional',
            userName: dbUser,
            service: bot.service,
            botUID: bot.botUID,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        const result = await postData(action)
        trackEvent(EVENTS.DATASET.CONNECT_DATABASE_COMPLETE, {
            api: {
                connector: 'Database',
                'action ': 'new connector flow',
                databaseName: driver.friendlyName,
                result: result?.status === 'success' ? 'Success' : 'Fail',
            },
        })
        return result
    }

    const updateBot = async () => {
        const JDBCObject = {
            driverClass: driver.className,
            server: serverHost,
            url: url,
            port: port,
            username: dbUser,
            password: dbPassword,
            database: database,
            query: sqlQuery,
        }
        const JDBC = JDBCObject
        const action = {
            action: 'updateRobot',
            inboxName: inboxName,
            inboxID: inboxID,
            label: reportName,
            description: reportDescription,
            workspaceID: workspaceID,
            reportURL: JDBC,
            isTransactional: isReportTransactional,
            userName: dbUser,
            service: bot.service,
            botUID: bot.botUID,
            enableMultipleSameDayLoads: allowMutipleLoads,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        const result = await postData(action)
        return result
    }

    const handleCreateBot = () => {
        createBot().then((results) => {
            if (results?.status === 'success') toast.success('Database bot created successfully')
            else if (results?.status === 'error')
                toast.error(results?.message ?? 'Failed to create Database Bot')
            setNextStep(false)
            stepBack()
            onClose()
        })
    }

    const handleUpdateBot = () => {
        updateBot().then((results) => {
            if (results?.status === 'success')
                toast.success('Database connector updated successfully')
            else if (results?.status === 'error')
                toast.error(results?.message ?? 'Failed to update Database connector')
            setNextStep(false)
            onClose()
        })
    }

    const handleSQLQueryChange = (event) => {
        setSqlQuery(event.target.value)
    }

    const handleOpenIntegrationRequest = async () => {
        const result = await integrationRequestModal.show({ title: 'Request database type' })
        if (result?.success) {
            toast.success('Your request has been submitted successfully!')
        }
    }

    // Convert driver names to options array for ScoopSelector
    const driverOptions = Object.keys(driverClassMapping).map((driverName) => ({
        label: driverName,
        value: driverName,
    }))

    // Tab definitions
    const tabs = [
        { key: '0', label: 'SQL Query' },
        { key: '1', label: 'Preview Results' },
    ]

    return (
        <>
            {!nextStep ? (
                <ScoopDialog
                    open={open}
                    title={'Database connector'}
                    icon={bot.icon}
                    onClose={onClose}
                    maxWidth="lg"
                    actions={
                        <>
                            {
                                <ScoopButton variant="text" size="sm" onClick={stepBack}>
                                    Back
                                </ScoopButton>
                            }
                            {isEditMode && (
                                <ScoopButton size="sm" onClick={handleRunBot}>
                                    Run Now
                                </ScoopButton>
                            )}
                            <ScoopButton
                                size="sm"
                                onClick={() => setNextStep(true)}
                                disabled={!queryVerified}
                            >
                                I've done this
                            </ScoopButton>
                        </>
                    }
                >
                    <div className={styles.dialogContent}>
                        <div className={styles.headerRow}>
                            <ScoopText className={styles.headerText}>
                                Provide your database connection information
                            </ScoopText>
                            <div className={styles.radioGroup}>
                                <ScoopRadioButton
                                    name="connectBy"
                                    value="Host"
                                    label="Host"
                                    checked={connectBy === 'Host'}
                                    onChange={(e) => setConnectBy(e.target.value)}
                                />
                                <ScoopRadioButton
                                    name="connectBy"
                                    value="URL"
                                    label="URL connection string"
                                    checked={connectBy === 'URL'}
                                    onChange={(e) => setConnectBy(e.target.value)}
                                />
                            </div>
                        </div>
                        <div>
                            {connectBy === 'URL' && (
                                <div className={styles.marginBottom}>
                                    <ScoopTextField
                                        padding="8px"
                                        label="URL"
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                    />
                                </div>
                            )}
                            {connectBy === 'Host' && (
                                <>
                                    <div className={styles.gridRowHostPort}>
                                        <ScoopTextField
                                            padding="8px"
                                            label="Server Host"
                                            value={serverHost}
                                            onChange={(e) => setServerHost(e.target.value)}
                                        />
                                        <ScoopTextField
                                            padding="8px"
                                            label="Port"
                                            value={port}
                                            onChange={(e) => setPort(e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.gridRow}>
                                        <div className={styles.selectorContainer}>
                                            <ScoopSelector
                                                label="Driver Name"
                                                value={driver.friendlyName}
                                                onChange={handleDriverChange}
                                                options={driverOptions}
                                            />
                                            <ScoopText className={styles.requestLink}>
                                                Can't find what you're looking for?{' '}
                                                <span
                                                    onClick={handleOpenIntegrationRequest}
                                                    className={styles.requestLinkText}
                                                >
                                                    Request an integration
                                                </span>
                                            </ScoopText>
                                        </div>
                                        <ScoopTextField
                                            padding="8px"
                                            label="Database"
                                            value={database}
                                            onChange={(e) => setDatabase(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}

                            <div className={styles.formControl}>
                                <div className={styles.gridRow}>
                                    <ScoopTextField
                                        padding="8px"
                                        label="User"
                                        value={dbUser}
                                        onChange={(e) => setDbUser(e.target.value)}
                                    />
                                    <ScoopTextField
                                        padding="8px"
                                        label="Password"
                                        type="password"
                                        value={dbPassword}
                                        onChange={(e) => setDBPassword(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className={styles.buttonRow}>
                                <ScoopButton size="sm" onClick={handleTestJDBC}>
                                    Test Connection
                                </ScoopButton>
                            </div>
                        </div>

                        <ScoopTab
                            tabs={tabs}
                            value={tabManager}
                            onChange={(e, newValue) => setTabManager(newValue)}
                            variant="underline"
                            fullWidth={false}
                        />
                        <ScoopTabPanel value="0" selectedValue={tabManager}>
                            <div className={styles.tabPanel}>
                                <ScoopText className={styles.sqlDescription}>
                                    Write a SQL query to fetch the data you need
                                </ScoopText>
                                <ScoopTextField
                                    value={sqlQuery}
                                    multiline
                                    rows={4}
                                    onChange={handleSQLQueryChange}
                                />
                                <div className={styles.testButtonContainer}>
                                    <ScoopButton size="sm" onClick={handleJDBCQuery}>
                                        Test SQL
                                    </ScoopButton>
                                </div>
                            </div>
                        </ScoopTabPanel>
                        <ScoopTabPanel value="1" selectedValue={tabManager}>
                            <div className={styles.tabPanel}>
                                <ScoopTable
                                    columns={columnDefs}
                                    rows={rowData}
                                    sorting={{ enabled: true }}
                                    height={400}
                                    domLayout="normal"
                                />
                            </div>
                        </ScoopTabPanel>
                        {/* Additional steps can be added similarly */}

                        {isLoading && (
                            <ScoopLinearProgress
                                variant="indeterminate"
                                className={styles.progressBar}
                            />
                        )}
                    </div>
                </ScoopDialog>
            ) : (
                <ScoopDialog
                    open={open}
                    title={'New Connector from Database'}
                    icon={bot.icon}
                    onClose={onClose}
                    actions={
                        <>
                            <ScoopButton
                                variant="text"
                                size="sm"
                                onClick={() => setNextStep(false)}
                            >
                                Back
                            </ScoopButton>
                            {isEditMode && (
                                <ScoopButton size="sm" onClick={() => handleUpdateBot()}>
                                    {' '}
                                    Update Connector{' '}
                                </ScoopButton>
                            )}
                            {!isEditMode && (
                                <ScoopButton size="sm" onClick={() => handleCreateBot()}>
                                    {' '}
                                    Create Connector{' '}
                                </ScoopButton>
                            )}
                        </>
                    }
                    className={styles.dialogNarrow}
                >
                    <div className={styles.infoBox}>
                        <ScoopText> data will be Scooped from this database daily</ScoopText>
                    </div>
                    <ScoopTextField
                        label={'Connector name'}
                        onChange={(event) => {
                            setReportName(event.target.value)
                        }}
                        value={reportName}
                    />
                    <ScoopTextField
                        multiline
                        rows={2}
                        label={'Connector description'}
                        onChange={(event) => {
                            setReportDescription(event.target.value)
                        }}
                        value={reportDescription}
                    />
                </ScoopDialog>
            )}
        </>
    )
}
